import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit, Trash2, GripVertical } from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';

const empty = { name: '', slug: '', description: '', image: '', order: 0, published: true };

const AdminCategories = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.listCategories().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c.id); setForm({ ...empty, ...c }); setOpen(true); };
  const save = async () => {
    if (!form.name || !form.slug) return toast.error('Name and slug are required');
    try {
      if (editing) await api.updateCategory(editing, form);
      else await api.createCategory(form);
      toast.success(editing ? 'Updated' : 'Created');
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try { await api.deleteCategory(id); toast.success('Deleted'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-zinc-400">Manage your storefront categories — these appear in navigation, filters, and product detail breadcrumbs.</p>
        <Button onClick={openNew} className="btn-gold"><Plus className="h-4 w-4 mr-2" /> New category</Button>
      </div>

      <Card className="bg-zinc-950 border-zinc-900 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60">
            <tr className="text-left text-zinc-400"><th className="p-3 w-10"></th><th className="p-3">Name</th><th className="p-3">Slug</th><th className="p-3">Description</th><th className="p-3">Order</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-zinc-500">No categories yet</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-zinc-900 hover:bg-zinc-900/30">
                <td className="p-3 text-zinc-600"><GripVertical className="h-4 w-4" /></td>
                <td className="p-3"><div className="flex items-center gap-3">{c.image && <img src={c.image} className="w-10 h-10 rounded object-cover" alt="" />}<p className="text-zinc-100">{c.name}</p></div></td>
                <td className="p-3 text-zinc-500 font-mono text-xs">/{c.slug}</td>
                <td className="p-3 text-zinc-400 max-w-md truncate">{c.description}</td>
                <td className="p-3 text-zinc-300">{c.order}</td>
                <td className="p-3"><Badge variant="outline" className={c.published ? 'border-emerald-600 text-emerald-400' : 'border-zinc-700 text-zinc-400'}>{c.published ? 'Published' : 'Draft'}</Badge></td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" className="text-zinc-300 hover:text-[#d4af37]" onClick={() => openEdit(c)}><Edit className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-zinc-300 hover:text-rose-500" onClick={() => del(c.id)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-900 text-zinc-100 max-w-xl">
          <DialogHeader><DialogTitle className="serif-italic text-[#d4af37] text-2xl">{editing ? 'Edit category' : 'New category'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') })} className="bg-transparent border-zinc-800 mt-1" /></div>
            <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /><p className="text-zinc-500 text-xs mt-1">URL-friendly. Lowercase, hyphens only.</p></div>
            <div><Label>Image URL</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://…" className="bg-transparent border-zinc-800 mt-1" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /></div>
            <div><Label>Display order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="bg-transparent border-zinc-800 mt-1" /></div>
            <div className="flex items-center justify-between border border-zinc-900 rounded p-3"><span className="text-zinc-300 text-sm">Published</span><Switch checked={!!form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} /></div>
          </div>
          <div className="mt-6 flex justify-end gap-3"><Button variant="outline" className="btn-outline-gold" onClick={() => setOpen(false)}>Cancel</Button><Button className="btn-gold" onClick={save}>{editing ? 'Update' : 'Create'}</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCategories;
